import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import {
  useEffect, useRef, useState, JSX,
} from 'react';
import * as React from 'react';

import { useTranslation } from 'next-i18next';
import {
  ApiUploadedTextareaFileContent,
  fetcher,
  isUploadedFileContentOk,
} from '@utils/data-fetching';
import { DecorativeGroup, Input, Button } from '@components/common/Forms';
import { dataTestId } from '@services/data-test-id';
import {
  $createImageNode,
  ImageNode,
  ImagePayload,
} from '../nodes/ImageNode';


export type InsertImagePayload = Readonly<ImagePayload>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand('INSERT_IMAGE_COMMAND');

/**
 * @description image upload modal content
 * file input with submit button
 */
export function InsertImageUploadedDialogBody({
  onClick,
}: {
  onClick: (payload: InsertImagePayload) => void;
}) {
  const { t } = useTranslation('common');

  const [src, setSrc] = useState('');
  const [altText] = useState('');

  const isDisabled = src === '';

  // upload the file to the server and return the uploaded image url
  const uploadTextareaFile = (blobInfo: Blob): Promise<string | void | undefined> => {
    if (blobInfo.size < 104857600) {
      const formattedPayload = new FormData();
      formattedPayload.append('file', blobInfo);

      return fetcher<ApiUploadedTextareaFileContent>('/v1/tinymce/upload', 'ro', {
        method: 'POST', payload: formattedPayload,
      })
        .then((response) => {
          if (isUploadedFileContentOk(response)) {
            return response.url;
          }
          return undefined;
        })
        .catch(() => { })
        .finally(() => {});
    }
    // window alert if file is larger than 100MB
    // bjtoast is not used because it is barely visible when file upload pop-up is shown
    window.alert(t('upload.video.bad.size'));
    return Promise.resolve('');
  };

  const loadImage = (files: FileList | null) => {
    if (files !== null) {
      const file = files[0];
      const reader = new FileReader();
      uploadTextareaFile(file).then((url) => {
        reader.onload = () => {
          if (typeof url === 'string') {
            setSrc(url);
          }
          return '';
        };
        reader.readAsDataURL(file);
      }).catch(() => {});
    }
  };

  // image upload modal content
  return (
    <div className="p-5">
      <DecorativeGroup className="rounded p-1">
        <Input
          type="file"
          name="uploadImage"
          onChange={(e) => loadImage(e.target.files)}
          accept="image/*"
          {...dataTestId('lexicalEditor.inputFileUpload')}
        />
        <Button
          disabled={isDisabled}
          onClick={() => onClick({ altText, src })}
          size="sm"
          color="secondary"
          {...dataTestId('lexicalEditor.inputFileConfirmButton')}
        >
          {t('global.action.confirm')}
        </Button>
      </DecorativeGroup>
    </div>
  );
}

/**
 * @description image upload modal
 * trigger the image upload modal for file upload
 */
export function InsertImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  const hasModifier = useRef(false);

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const onClick = (payload: InsertImagePayload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  return (
    <>
      <InsertImageUploadedDialogBody onClick={onClick} />
    </>
  );
}


/**
 * @description image upload plugin
 * image upload and insert image into the editor
 */
export default function ImagesPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    // trigger the insert image command
    return mergeRegister(
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  return null;
}
